import React from "react";
// import { Search, Eye, Download } from "lucide-react";
import Search from '@mui/icons-material/Search';
import Eye from '@mui/icons-material/Visibility';
import Download from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { usePermission } from "../../../permissions/usePermission";

export default function HowToImpliment() {
const theme = useTheme();
const navigate = useNavigate();
const location = useLocation();
const { canByKey } = usePermission();
const canUpdateProgram = canByKey("PROGRAM_MANAGEMENT_PROGRAMS_UPDATE");
const [searchParams] = useSearchParams();
const [search, setSearch] = useState("");
const [fileTypeFilter, setFileTypeFilter] = useState("ALL");
const [apiResources, setApiResources] = useState([]);
const [previewItem, setPreviewItem] = useState(null);
const [docLoading, setDocLoading] = useState(false);
const [docTimedOut, setDocTimedOut] = useState(false);

const stateResources = useMemo(
  () => (Array.isArray(location.state?.resources) ? location.state.resources : []),
  [location.state],
);

const programIdFromState = location.state?.programId || null;
const programIdFromQuery = searchParams.get("programId");
const resolvedProgramId = programIdFromState || programIdFromQuery || null;

useEffect(() => {
  const fetchHowToResources = async () => {
    if (!resolvedProgramId || stateResources.length) return;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/programs/${resolvedProgramId}`,
      );
      const fromApi = Array.isArray(res?.data?.program?.howToImplement)
        ? res.data.program.howToImplement
        : [];
      setApiResources(fromApi);
    } catch (error) {
      console.error("Failed to fetch How To Implement resources", error);
      setApiResources([]);
    }
  };

  fetchHowToResources();
}, [resolvedProgramId, stateResources.length]);

const resources = useMemo(
  () => (stateResources.length ? stateResources : apiResources),
  [stateResources, apiResources],
);

const getNormalizedFileType = (item = {}) => {
  const type = String(item?.fileType || "").toUpperCase();
  if (type === "DOCUMENT" || type === "DOC") return "DOCUMENT";
  if (type === "IMAGE") return "IMAGE";
  if (type === "VIDEO") return "VIDEO";

  const url = String(item?.fileUrl || "").toLowerCase();
  if (/\.(png|jpg|jpeg|gif|webp|svg)(\?|$)/.test(url)) return "IMAGE";
  if (/\.(mp4|webm|ogg|mov)(\?|$)/.test(url)) return "VIDEO";
  return "DOCUMENT";
};

const filteredResources = useMemo(() => {
  const q = search.trim().toLowerCase();
  return resources.filter((item) => {
    const matchesSearch = !q || String(item?.name || "").toLowerCase().includes(q);
    const matchesType =
      fileTypeFilter === "ALL" || getNormalizedFileType(item) === fileTypeFilter;

    return matchesSearch && matchesType;
  });
}, [resources, search, fileTypeFilter]);

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getPreviewType = (item = {}) => {
  const type = String(item?.fileType || "").toUpperCase();
  if (type === "IMAGE") return "IMAGE";
  if (type === "VIDEO") return "VIDEO";

  const url = String(item?.fileUrl || "").toLowerCase();
  if (/\.(png|jpg|jpeg|gif|webp|svg)(\?|$)/.test(url)) return "IMAGE";
  if (/\.(mp4|webm|ogg|mov)(\?|$)/.test(url)) return "VIDEO";
  return "DOCUMENT";
};

const handleView = (item) => {
  if (!item?.fileUrl) return;
  setDocLoading(getPreviewType(item) === "DOCUMENT");
  setDocTimedOut(false);
  setPreviewItem(item);
};

const getDocumentPreviewUrl = (url = "") => {
  const lowerUrl = String(url).toLowerCase();
  const encoded = encodeURIComponent(url);

  if (/\.pdf(\?|$)/.test(lowerUrl)) {
    return `https://docs.google.com/gview?url=${encoded}&embedded=true`;
  }

  if (/\.(doc|docx|ppt|pptx|xls|xlsx)(\?|$)/.test(lowerUrl)) {
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encoded}`;
  }

  return `https://docs.google.com/gview?url=${encoded}&embedded=true`;
};

useEffect(() => {
  if (!(previewItem && getPreviewType(previewItem) === "DOCUMENT" && docLoading)) {
    return undefined;
  }

  const timer = setTimeout(() => {
    setDocTimedOut(true);
  }, 6000);

  return () => clearTimeout(timer);
}, [previewItem, docLoading]);

const triggerDownload = (url, fileName) => {
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName || "resource");
  link.target = "_self";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const handleDownload = async (url, fileName) => {
  if (!url) return;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error("Unable to fetch file");
    }
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    triggerDownload(blobUrl, fileName);
    window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
  } catch (error) {
    console.error("Failed to force download:", error);
  }
};

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.palette.background.slate }}>
      <div className="px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4 md:py-6 lg:py-8">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
          
          {/* Left */}
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
            <button onClick={() => navigate(-1)} className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border flex items-center justify-center flex-shrink-0">
              <ArrowBackIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
            </button>

            <div className="min-w-0">
              <h1 className="text-base sm:text-lg md:text-xl font-semibold truncate">
                How to implement
              </h1>
              <p className="text-gray-500 text-xs sm:text-sm truncate">
                SOP and Guidelines
              </p>
            </div>
          </div>

          {/* Right Controls */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto md:w-1/2">
            
            {/* Search */}
            <div className="flex items-center border rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 bg-white flex-1 sm:flex-none md:flex-1">
              <Search size={14} className="text-gray-400 mr-1 sm:mr-2 flex-shrink-0" />
              <input
                type="text"
                placeholder="Search"
                className="outline-none text-xs sm:text-sm flex-1 min-w-0"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Sort */}
            <select
              className="border rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 bg-white text-xs sm:text-sm flex-1 sm:flex-none md:flex-1"
              value={fileTypeFilter}
              onChange={(e) => setFileTypeFilter(e.target.value)}
            >
              <option value="ALL">All Types</option>
              <option value="DOCUMENT">Document</option>
              <option value="IMAGE">Image</option>
              <option value="VIDEO">Video</option>
            </select>

            {canUpdateProgram && (
            <button
              type="button"
              onClick={() => resolvedProgramId && navigate(`/editProgram/${resolvedProgramId}`)}
              disabled={!resolvedProgramId}
              style={{
                flex: "1 1 auto",
                borderRadius: "8px",
                padding: "6px 12px",
                fontSize: "14px",
                fontWeight: 500,
                border: `2px solid ${theme.palette.secondary.figma}`,
                backgroundColor: "white",
                color: theme.palette.secondary.figma,
                cursor: !resolvedProgramId ? "not-allowed" : "pointer",
                opacity: !resolvedProgramId ? 0.5 : 1,
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor = theme.palette.secondary.figma;
                  e.currentTarget.style.color = "white";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "white";
                e.currentTarget.style.color = theme.palette.secondary.figma;
              }}
              className="sm:flex-none md:flex-1"
            >
              Update
            </button>
            )}
          </div>
        </div>

        <div className="rounded-xl overflow-x-auto border" style={{ backgroundColor: theme.palette.background.default }}>
          
          {/* Mobile Card View (below md) / Table View (md and above) */}
          <div className="hidden md:block">
            {/* Table Header */}
            <div className="grid grid-cols-5 text-gray-600 text-xs lg:text-sm px-4 lg:px-6 py-2 lg:py-3 sticky top-0" style={{ backgroundColor: theme.palette.background.slate }}>
              <div>NAME</div>
              <div>DATE</div>
              <div>FILETYPE</div>
              <div>VIEW</div>
              <div>DOWNLOAD</div>
            </div>

            {/* Table Rows */}
            {filteredResources.map((item, index) => (
              <div
                key={`${item?.fileUrl || "resource"}-${index}`}
                className="grid grid-cols-5 px-4 lg:px-6 py-3 lg:py-4 border-t items-center hover:bg-gray-50 transition-colors"
              >
                <div className="font-medium text-xs lg:text-sm truncate">{item?.name || "-"}</div>
                <div className="text-gray-600 text-xs lg:text-sm">{formatDate(item?.createdAt)}</div>
                <div className="text-gray-600 text-xs lg:text-sm">{item?.fileType || "-"}</div>

                {/* View Icon */}
                <div>
                  <Eye className="text-gray-600 cursor-pointer hover:text-blue-600 transition-colors" size={18} onClick={() => handleView(item)} />
                </div>

                {/* Download Icon */}
                <div>
                  <Download className="text-gray-600 cursor-pointer hover:text-blue-600 transition-colors" size={18} onClick={() => handleDownload(item?.fileUrl, item?.name)} />
                </div>
              </div>
            ))}

            {filteredResources.length === 0 && (
              <div className="px-4 lg:px-6 py-8 border-t text-center text-gray-500 text-sm">
                No implementation resources available.
              </div>
            )}
          </div>

          {/* Mobile Card View (below md) */}
          <div className="md:hidden">
            {filteredResources.length === 0 ? (
              <div className="px-3 sm:px-4 py-6 sm:py-8 text-center text-gray-500 text-xs sm:text-sm">
                No implementation resources available.
              </div>
            ) : (
              <div className="divide-y">
                {filteredResources.map((item, index) => (
                  <div
                    key={`${item?.fileUrl || "resource"}-${index}`}
                    className="px-3 sm:px-4 py-3 sm:py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex flex-col gap-2">
                      {/* Name */}
                      <div className="font-medium text-xs sm:text-sm break-words">{item?.name || "-"}</div>
                      
                      {/* Info Row */}
                      <div className="flex flex-col xs:flex-row xs:justify-between gap-1.5 xs:gap-3 text-gray-600 text-xs">
                        <span>{formatDate(item?.createdAt)}</span>
                        <span>{item?.fileType || "-"}</span>
                      </div>

                      {/* Action Icons */}
                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 pt-2 border-t border-gray-200">
                        <button
                          onClick={() => handleView(item)}
                          className="flex items-center gap-1 text-gray-600 hover:text-blue-600 transition-colors text-xs"
                        >
                          <Eye size={16} />
                          <span>View</span>
                        </button>
                        <button
                          onClick={() => handleDownload(item?.fileUrl, item?.name)}
                          className="flex items-center gap-1 text-gray-600 hover:text-blue-600 transition-colors text-xs"
                        >
                          <Download size={16} />
                          <span>Download</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog
        open={Boolean(previewItem)}
        onClose={() => {
          setPreviewItem(null);
          setDocLoading(false);
          setDocTimedOut(false);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            margin: { xs: "16px", sm: "32px" },
          },
        }}
      >
        <DialogTitle sx={{ p: { xs: 1, sm: 2 } }}>
          <IconButton
            onClick={() => {
              setPreviewItem(null);
              setDocLoading(false);
              setDocTimedOut(false);
            }}
            sx={{ position: "absolute", right: 8, top: 6 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            height: { xs: "60vh", sm: "70vh", md: "75vh" },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            bgcolor: "#fff",
            p: { xs: 1, sm: 2 },
          }}
        >
          {previewItem && getPreviewType(previewItem) === "IMAGE" && (
            <img
              src={previewItem.fileUrl}
              alt={previewItem.name || "Preview"}
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          )}

          {previewItem && getPreviewType(previewItem) === "VIDEO" && (
            <video
              src={previewItem.fileUrl}
              controls
              autoPlay
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          )}

          {previewItem && getPreviewType(previewItem) === "DOCUMENT" && (
            <>
              {docLoading && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#fff",
                    zIndex: 1,
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
              {docTimedOut && (
                <Box
                  sx={{
                    position: "absolute",
                    left: { xs: 8, sm: 12 },
                    right: { xs: 8, sm: 12 },
                    bottom: { xs: 8, sm: 12 },
                    zIndex: 2,
                    bgcolor: "rgba(255,255,255,0.95)",
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    p: { xs: 1, sm: 1.5 },
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "stretch", sm: "center" },
                    gap: { xs: 1, sm: 1 },
                  }}
                >
                  <span style={{ fontSize: "12px", color: "#555" }}>
                    Preview is taking longer than usual.
                  </span>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      window.open(previewItem?.fileUrl, "_blank", "noopener,noreferrer")
                    }
                    sx={{ whiteSpace: "nowrap", fontSize: "11px", px: 1 }}
                  >
                    Open Directly
                  </Button>
                </Box>
              )}
              <iframe
                title="How To Implement Document Preview"
                src={getDocumentPreviewUrl(previewItem.fileUrl)}
                onLoad={() => {
                  setDocLoading(false);
                  setDocTimedOut(false);
                }}
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1, sm: 2 }, gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleDownload(previewItem?.fileUrl, previewItem?.name)}
            sx={{ fontSize: { xs: "11px", sm: "13px" } }}
          >
            Download
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setPreviewItem(null);
              setDocLoading(false);
              setDocTimedOut(false);
            }}
            sx={{ fontSize: { xs: "11px", sm: "13px" } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
